import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import {FmlxButton, FmlxIcon, FmlxTable} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {connect, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import moment from 'moment';
import {Button} from 'react-bootstrap';
import JSZip from 'jszip';
import {checkLoadCellError} from 'stores/business/loadCellErrorBusiness';
import {openPlateDefinitionPage} from 'stores/business/menuBusiness';

import {removePlatesFromLibrary} from 'stores/business/plateRecognitionBusiness';
import {selectIsPlateRecognitionEnabled} from 'stores/selectors/plateRecognitionSelectors';
import {useStaticCallback} from 'lib/customHooks';
import {DrawerButtonTitle} from '../Common';

import RemoveLabwareModal from './RemoveLabwareModal';
import {HeaderLayout} from '../../components';
import DeepWellLabel from '../../components/DeepWellLabel';
import PageLayout from '../../components/Layout/PageLayout';

import {DefaultWellValue, Feedback, PlateType, TrayType, ViewMode, WellCount} from '../../constants/dataEnum';

import withIntl from '../../lib/withIntl';
import {checkContainIllegalChars} from '../../lib/stringHelper';

import {changeView, hideLoading, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {hideModal, showModal} from '../../stores/actions/modalStateAction';
import {setLastProcessedId, restorePlateTypeEditorState} from '../../stores/actions/plateTypeEditorStateAction';

import {isLabwareNameAlreadyExist} from '../../stores/business/labwareBusinessAction';
import {
  createLabwareDefinitions,
  readLabwareDefinitions,
  updateLabwareDefinition,
  deleteLabwareDefinitions,
  duplicateLabwareDefinitions,
  refreshLabwareDefinitions,
} from '../../stores/business/labwareDatabaseBusiness';
import {showModalUnsupportedTray, showUpdatePlateDefinitionDialog} from '../../stores/business/commonModalBusiness';

import './LabwareManager.scss';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {proceedReteachImportedLabware} from '../../stores/business/reteachPlateBusiness';
import {addTemporaryState} from '../../stores/actions/temporaryStateAction';
import {showPlateIsNotEditableModal} from '../../stores/business/modalStateBusinessAction';
import {defaultPlatesIdList} from '../PlateTypeEditor/PlateTypeEditorEnum';

const HEADER_STYLE_8 = {width: '8%', maxWidth: '8%', minWidth: '8%'};
const HEADER_STYLE_10 = {width: '10%', maxWidth: '10%', minWidth: '10%'};
const HEADER_STYLE_12 = {width: '12%', maxWidth: '12%', minWidth: '12%'};

const COLUMN_KEY_MAP = {
  plateName: 'Name',
  numberOfWell: 'WellNumber',
  wellBottomProfile: 'WellBottomShape',
  wellShape: 'WellSurfaceShape',
  wellCapacity: 'WellCapacity',
  maxVolume: 'WorkingCapacity',
  notes: 'Notes',
  dateModified: 'LastModifiedTime',
};

const mapStateToProps = (state) => ({
  plateTypeEditorStateLastProcessedId: state.plateTypeEditorState.lastProcessedId,
  labwareConfig: state.configState.labwareConfig,
  deepWellThreshold: state.configState.deepWellThreshold,
  trayType: state.trayCalibrationState.trayType,
});

const mapDispatchToProps = (dispatch) => ({
  onChangeView: bindActionCreators(changeView, dispatch),
  onHideLoading: bindActionCreators(hideLoading, dispatch),
  onShowLoading: bindActionCreators(showLoading, dispatch),
  onShowSnackbar: bindActionCreators(showSnackbar, dispatch),
  modalStateActionShowModal: bindActionCreators(showModal, dispatch),
  modalStateActionHideModal: bindActionCreators(hideModal, dispatch),
  onSetLastProcessedId: bindActionCreators(setLastProcessedId, dispatch),
  onRestorePlateTypeEditorState: bindActionCreators(restorePlateTypeEditorState, dispatch),
  onFastGetInstalledTrayAndOpenPlateDefinitionPage: bindActionCreators(openPlateDefinitionPage, dispatch),
  labwareBusinessIsLabwareNameAlreadyExist: bindActionCreators(isLabwareNameAlreadyExist, dispatch),
  labwareDatabaseBusinessCreateLabwareDefinitions: bindActionCreators(createLabwareDefinitions, dispatch),
  labwareDatabaseBusinessReadLabwareDefinitions: bindActionCreators(readLabwareDefinitions, dispatch),
  labwareDatabaseBusinessUpdateLabwareDefinition: bindActionCreators(updateLabwareDefinition, dispatch),
  labwareDatabaseBusinessDeleteLabwareDefinitions: bindActionCreators(deleteLabwareDefinitions, dispatch),
  labwareDatabaseBusinessDuplicateLabwareDefinitions: bindActionCreators(duplicateLabwareDefinitions, dispatch),
  labwareDatabaseBusinessRefreshLabwareDefinitions: bindActionCreators(refreshLabwareDefinitions, dispatch),
  commonModalBusinessShowModalUnsupportedTray: bindActionCreators(showModalUnsupportedTray, dispatch),
  commonModalBusinessShowUpdatePlateDefinitionDialog: bindActionCreators(showUpdatePlateDefinitionDialog, dispatch),
  reteachPlateBusinessProceedReteachImportedLabware: bindActionCreators(proceedReteachImportedLabware, dispatch),
});

function LabwareManager({
  plateTypeEditorStateLastProcessedId,
  labwareConfig,
  deepWellThreshold,
  trayType,
  onChangeView,
  onHideLoading,
  onShowLoading,
  onShowSnackbar,
  modalStateActionShowModal,
  modalStateActionHideModal,
  onSetLastProcessedId,
  onRestorePlateTypeEditorState,
  onFastGetInstalledTrayAndOpenPlateDefinitionPage,
  labwareBusinessIsLabwareNameAlreadyExist,
  labwareDatabaseBusinessCreateLabwareDefinitions,
  labwareDatabaseBusinessReadLabwareDefinitions,
  labwareDatabaseBusinessUpdateLabwareDefinition,
  labwareDatabaseBusinessDeleteLabwareDefinitions,
  labwareDatabaseBusinessDuplicateLabwareDefinitions,
  labwareDatabaseBusinessRefreshLabwareDefinitions,
  commonModalBusinessShowModalUnsupportedTray,
  commonModalBusinessShowUpdatePlateDefinitionDialog,
  reteachPlateBusinessProceedReteachImportedLabware,
  intl,
}) {
  const [dataSource, setDataSource] = useState([]);
  const [dataSourceTotalLength, setDataSourceTotalLength] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortValue, setSortValue] = useState(null);
  const [sortType, setSortType] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageIndexCount, setPageIndexCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeFormat = [10, 20, 30, 40, 50];
  const [isLoading, setIsLoading] = useState(false);
  const [filterMap, setFilterMap] = useState({});
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [dataRemoveModal, setDataRemoveModal] = useState([]);

  const [refetch, setRefetch] = useState(null);
  const [targetSelectedRowIdsAfterRefetch, setTargetSelectedRowIdsAfterRefetch] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const isPlateRecognitionEnabled = useSelector(selectIsPlateRecognitionEnabled);

  // WARNING: USE AS LAST RESORT.
  // refactor the FmlxTable into FmlxTableCompound so we won't need to do any force remount
  const [forceRemountCount, setForceRemountCount] = useState(0);

  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  const triggerRefetch = (index) => {
    if (!index) {
      setPageIndex(0);
    }

    setTimeout(() => {
      setRefetch(new Date().toString());
    }, 1);
  };

  // TODO: Move this function and all the logic to business -> make it reusable for reteach
  const createNewHandler = async () => {
    const isLoadCellError = await dispatch(checkLoadCellError(null, createNewHandler));
    if (isLoadCellError) return;
    if (trayType !== TrayType.NORMAL_CADDY && trayType !== TrayType.DOUBLE_CADDY) {
      commonModalBusinessShowModalUnsupportedTray(true, ViewMode.PLATE_TYPE_EDITOR);
      return;
    }

    await onFastGetInstalledTrayAndOpenPlateDefinitionPage();
  };

  const deleteHandler = async (ids, labwareName = null) => {
    onShowLoading();

    const result = await labwareDatabaseBusinessDeleteLabwareDefinitions(ids);

    onHideLoading();

    if (!result || result?.code !== 0) {
      onShowSnackbar(Feedback.ERROR, result?.description ?? 'Something happened');

      return;
    }

    const labwareNotDeletedList = result?.payload?.labwareNotDeletedList;
    dispatch(removePlatesFromLibrary(ids, labwareNotDeletedList));

    if (result && labwareNotDeletedList.length) {
      const newData = labwareNotDeletedList.map((data) => ({
        id: data.labwareId,
        plateName: labwareName ?? dataSource.find((e) => e.id === data.labwareId)?.plateName ?? '',
        usedInProtocol: data.usedInProtocols.join(', '),
      }));
      setDataRemoveModal(newData);
      setShowRemoveModal(true);

      if (labwareNotDeletedList.length === ids.length) return;
    }

    triggerRefetch(0);

    let snackbarMessage = '';

    if (labwareNotDeletedList.length === 0) {
      snackbarMessage = intl.formatMessage({id: 'labwareManager.delete.notification.successAllSelected'});
    } else {
      snackbarMessage = (
        <FormattedHTMLMessage
          id="labwareManager.delete.notification.success"
          values={{deletedLabware: ids.length - labwareNotDeletedList.length, totalLabware: ids.length}}
        />
      );
    }

    onShowSnackbar(Feedback.SUCCESS, snackbarMessage);

    labwareDatabaseBusinessRefreshLabwareDefinitions();
  };

  const showDeleteConfirmation = (ids, labwareName) => {
    const title = intl.formatMessage({id: 'labwareManager.deleteModal.title'});
    const content = intl.formatMessage(
      {id: 'labwareManager.deleteModal.content'},
      {count: `${ids.length} Plate${ids.length > 1 ? 's' : ''}`},
    );

    modalStateActionShowModal(
      title,
      content,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          text: intl.formatMessage({id: 'labwareManager.deleteModal.ok'}),
          handlerClick: () => {
            modalStateActionHideModal();
            deleteHandler(ids, labwareName);
          },
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          text: intl.formatMessage({id: 'labwareManager.deleteModal.cancel'}),
          handlerClick: () => {
            modalStateActionHideModal();
          },
        },
      ],
      () => modalStateActionHideModal(),
      '',
      true,
    );
  };

  const duplicateHandler = async (ids) => {
    const result = await labwareDatabaseBusinessDuplicateLabwareDefinitions(ids);

    if (!result || result?.code !== 0) {
      onShowSnackbar(Feedback.ERROR, result?.description ?? 'Something happened');

      return;
    }

    const newData = result.payload;

    setTargetSelectedRowIdsAfterRefetch(newData.map((e) => e.id));
    setForceRemountCount(forceRemountCount + 1);

    onShowSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'labwareManager.duplicate.notification.success'}));

    labwareDatabaseBusinessRefreshLabwareDefinitions();
  };

  const doEditRowHandler = async (labwareDefinition) => {
    const {
      id,
      wellBottomData,
      wellSurfaceData,
      description,
      partNumber,
      vendor,
      notes,
      rowPitch,
      columnPitch,
      wellCapacity,
      workingCapacity,
      firstWellOffset,
      wellNumber,
      type,
      wellLabel,
    } = labwareDefinition;
    const {Shape: wellBottomDataShape, WellDepth: depth} = wellBottomData;
    const {Shape: wellSurfaceDataShape, Width: width, Length: length, Diameter: diameter} = wellSurfaceData;
    const {x, y, z} = firstWellOffset;
    let troughDepth = 0;
    let colPitchAssaySample = 0;

    if (type === PlateType.IFC) {
      if (troughDepth === 0) {
        troughDepth = depth - 0.5;
        switch (wellNumber) {
          case WellCount.WELL_IFC_192_24:
            colPitchAssaySample = DefaultWellValue.WELL_IFC_192_24.columnPitchAssaySample;
            break;
          default:
            break;
        }
      }
    }

    const labwareDefinitionData = {
      id,
      currentWizardView: isPlateRecognitionEnabled ? 4 : 3,
      allowedToNext: true,
      processedStep: 0,
      wellNumber,
      wellLabel,
      wellBottomProfile: wellBottomDataShape || 'Undefined',
      wellShape: wellSurfaceDataShape || 'Undefined',
      plateName: description,
      partNumber,
      vendor,
      notes,
      height: z,
      depth,
      width,
      length: wellSurfaceDataShape === 'Circle' ? diameter : length,
      columnOrigin: y,
      rowOrigin: x,
      columnPitch,
      rowPitch,
      capacity: wellCapacity,
      workingVolume: workingCapacity,
      plateType: type,
      troughDepth,
      colPitchAssaySample,
    };

    onRestorePlateTypeEditorState(labwareDefinitionData);

    dispatch(addTemporaryState('plateTypeEditorState', labwareDefinitionData));
    if (defaultPlatesIdList.includes(id)) {
      dispatch(showPlateIsNotEditableModal());
    }
    onChangeView(ViewMode.PLATE_TYPE_EDITOR);
  };

  const editRowHandler = useStaticCallback(
    (e, labwareDefinition) => {
      e.stopPropagation();
      doEditRowHandler(labwareDefinition);
    },
    [dispatch, isPlateRecognitionEnabled],
  );

  const duplicateRowHandler = (e, id) => {
    e.stopPropagation();

    duplicateHandler([id]);
  };

  const deleteRowHandler = (e, id, labwareName) => {
    e.stopPropagation();

    showDeleteConfirmation([id], labwareName);
  };

  const generateData = (e) => ({
    _original: e,
    id: e.id,
    plateName: e.description,
    numberOfWell: e.type === PlateType.MICROPLATE ? e.rowCount * e.colCount : e.wellNumber,
    wellBottomProfile: e.wellBottomData.Shape,
    wellShape: e.wellSurfaceData.Shape,
    wellCapacity: e.wellCapacity,
    maxVolume: e.workingCapacity,
    notes: e.notes,
    dateModified: e.lastModifiedTime ? moment(e.lastModifiedTime).toDate() : '',
    actionButtons: (
      <div style={{display: 'flex', alignItems: 'center', width: '100%', fontSize: 16}}>
        <FmlxButton
          type="basic"
          onClick={(ee) => {
            editRowHandler(ee, e);
          }}
          onlyIcon
          variant="plain"
          icon={<FmlxIcon name="Pencil" size="sm" />}
          withTooltip
          tooltipDescription="Edit"
        />
        &nbsp;&nbsp;
        <FmlxButton
          type="basic"
          onClick={(ee) => {
            duplicateRowHandler(ee, e.id);
          }}
          onlyIcon
          variant="plain"
          icon={<FmlxIcon name="Duplicate" size="sm" />}
          withTooltip
          tooltipDescription="Duplicate"
        />
        &nbsp;&nbsp;
        <FmlxButton
          type="basic"
          onClick={(ee) => {
            deleteRowHandler(ee, e.id, e.description);
          }}
          onlyIcon
          variant="plain"
          icon={<FmlxIcon name="Trash" size="sm" />}
          withTooltip
          tooltipDescription="Delete"
        />
      </div>
    ),
  });

  const fetchData = async (pageSizeArg, pageIndexArg) => {
    setIsLoading(true);

    const sort = {
      key: COLUMN_KEY_MAP.dateModified,
      value: 'Descending',
    };

    if (sortValue && sortType) {
      sort.key = COLUMN_KEY_MAP[sortValue] ?? sortValue;
      sort.value = sortType === FmlxTable.SortType.ASC ? 'Ascending' : 'Descending';
    }

    let plateNameFilterIndex = -1;
    let isDeepWell = false;
    let filterValue = null;

    let filter = Object.keys(filterMap)
      .filter((e) => filterMap[e])
      .map((e, i) => {
        const filterKey = COLUMN_KEY_MAP[e] ?? e;
        filterValue = filterMap[e];

        switch (filterKey) {
          case COLUMN_KEY_MAP.dateModified:
            filterValue = filterValue ? moment(filterValue).format('YYYY-MM-DD') : '';

            break;
          case COLUMN_KEY_MAP.wellBottomProfile:
            switch (filterValue) {
              case 'All':
                filterValue = null;
                break;
              case 'U':
                filterValue = '2';
                break;
              case 'V':
                filterValue = '3';
                break;
              case 'Flat':
                filterValue = '1';
                break;
              default:
                filterValue = '0';
                break;
            }

            break;
          case COLUMN_KEY_MAP.wellShape:
            switch (filterValue) {
              case 'All':
                filterValue = null;
                break;
              case 'Circle':
                filterValue = '1';
                break;
              case 'Rectangle':
                filterValue = '2';
                break;
              default:
                filterValue = '0';
                break;
            }

            break;
          default:
            break;
        }

        if (filterKey === COLUMN_KEY_MAP.plateName) {
          if (filterValue && filterValue.toLowerCase().includes('dw')) {
            plateNameFilterIndex = i;
            isDeepWell = true;
          }
        }

        return [
          {
            key: filterKey,
            value: filterValue,
            operation: '=',
          },
        ];
      });

    if (isDeepWell) {
      if (plateNameFilterIndex !== -1) {
        filter[plateNameFilterIndex].push({
          key: 'WellDepth',
          value: deepWellThreshold,
          operation: '>=',
        });
      } else {
        const deepWellFilter = {
          key: 'WellDepth',
          value: deepWellThreshold,
          operation: '>=',
        };

        filter = [...filter, [deepWellFilter]];
      }
    }

    filter = [
      ...filter,
      [
        {
          key: 'ParentId',
          operation: 'IS NULL',
        },
      ],
    ];

    const result = await labwareDatabaseBusinessReadLabwareDefinitions(
      pageIndexArg ?? pageIndex,
      pageSizeArg ?? pageSize,
      sort,
      filter,
    );

    let data = [];
    let totalLength = 0;

    if (result?.items) {
      data = result.items.map((e) => generateData(e));
      totalLength = result.totalCount;
    }

    const newSelectedRowIds = [];

    if (targetSelectedRowIdsAfterRefetch.length) {
      data.forEach((e, index) => {
        if (targetSelectedRowIdsAfterRefetch.includes(e.id)) {
          newSelectedRowIds.push(index);
        }
      });
    }

    if (plateTypeEditorStateLastProcessedId) {
      const selectedRowIndex = data.findIndex((e) => e.id === plateTypeEditorStateLastProcessedId.id);

      if (selectedRowIndex >= 0 && !newSelectedRowIds.includes(selectedRowIndex)) {
        newSelectedRowIds.push(selectedRowIndex);
      }
    }

    setSelectedRowIds(newSelectedRowIds);
    setDataSource(data);
    setDataSourceTotalLength(totalLength);
    setTargetSelectedRowIdsAfterRefetch([]);
    if (result?.totalPages !== undefined) setPageIndexCount(result?.totalPages);
    if (result?.pageIndex !== undefined) setPageIndex(result?.pageIndex);

    setTimeout(() => {
      setIsLoading(false);

      if (plateTypeEditorStateLastProcessedId) {
        onShowSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({
            id: plateTypeEditorStateLastProcessedId.isEdit
              ? 'plateTypeEditorContainer.updateLabware.notification.success'
              : 'plateTypeEditorContainer.createLabware.notification.success',
          }),
        );

        onSetLastProcessedId(null);
      }
    }, 1000);
  };

  const fetchDataCallback = useCallback(
    (objParam) => {
      fetchData(objParam.pageSize, objParam.pageIndex);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterMap, sortValue, sortType],
  );

  useEffect(() => {
    if (!refetch) {
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  const onDoubleClickRow = (e) => {
    doEditRowHandler(e._original);
  };

  const importHandler = async (e) => {
    const toText = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.readAsText(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

    if (!e.target.files.length) {
      return;
    }

    const files = [];
    const jsonFiles = [];

    for (let idx = 0; idx < e.target.files.length; idx += 1) {
      const file = e.target.files[idx];
      files.push(file);
    }

    const promises = files.map(async (file, i) => {
      jsonFiles[i] = await toText(file);
    });

    await Promise.all(promises);

    let labwareDefinitions;

    try {
      labwareDefinitions = jsonFiles.map((ee) => JSON.parse(ee));
    } catch (err) {
      console.log(err);

      onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.import.notification.error'}));

      return;
    }

    const result = await labwareDatabaseBusinessCreateLabwareDefinitions(labwareDefinitions);

    if (!result) {
      onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.import.notification.error'}));

      return;
    }

    if (result?.code !== 0) {
      onShowSnackbar(Feedback.ERROR, result?.description);

      return;
    }

    setTargetSelectedRowIdsAfterRefetch(result.payload.map((item) => item.id));
    triggerRefetch(pageIndex);

    onShowSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'labwareManager.import.notification.success'}));

    labwareDatabaseBusinessRefreshLabwareDefinitions();

    // Show Reteach Modal
    if (result.payload?.length > 0) {
      commonModalBusinessShowUpdatePlateDefinitionDialog(
        intl.formatMessage({
          id: 'modal.showUpdatePlateDefinitionDialog.title',
        }),
        () => reteachPlateBusinessProceedReteachImportedLabware(result.payload),
      );
    }
  };

  const exportHandler = () => {
    const labwareDefinitions = selectedRows.map((e) => e._original);

    if (labwareDefinitions.length === 1) {
      const url = window.URL.createObjectURL(new Blob([JSON.stringify(labwareDefinitions[0])]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${labwareDefinitions[0].description}.json`);
      document.body.appendChild(link);
      link.click();
    } else {
      const zip = new JSZip();

      labwareDefinitions.forEach((e) => {
        zip.file(`${e.description}.json`, JSON.stringify(e));
      });

      zip.generateAsync({type: 'blob'}).then((content) => {
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Labware Definitions.zip');
        document.body.appendChild(link);
        link.click();
      });
    }

    onShowSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'labwareManager.export.notification.success'}));
  };

  // inline editing
  const onChangeCell = async (columnId, rowIndex, value) => {
    const {_original: editedLabwareDefinition} = dataSource[rowIndex];
    if (defaultPlatesIdList.includes(editedLabwareDefinition.id)) {
      dispatch(showPlateIsNotEditableModal());
      return true;
    }

    const newValue = value.trim();

    switch (columnId) {
      case 'plateName':
        if (!newValue) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.required'}));

          return true;
        }

        if (editedLabwareDefinition.description === newValue) {
          return true;
        }

        if (checkContainIllegalChars(newValue)) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.illegalCharacters'}));

          return true;
        }

        if (labwareBusinessIsLabwareNameAlreadyExist(newValue, editedLabwareDefinition.id)) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.nameAlreadyExists'}));

          return true;
        }

        if (newValue.length > labwareConfig.nameMaxLength) {
          onShowSnackbar(
            Feedback.ERROR,
            intl.formatMessage(
              {id: 'labwareManager.edit.warning.nameMaxLength'},
              {maxLength: labwareConfig.nameMaxLength},
            ),
          );

          return true;
        }

        editedLabwareDefinition.description = newValue;
        break;
      case 'wellCapacity':
        if (editedLabwareDefinition.wellCapacity === newValue) {
          return true;
        }

        // eslint-disable-next-line no-restricted-globals
        if (!(!isNaN(parseFloat(newValue)) && isFinite(newValue))) {
          return true;
        }

        if (newValue < 0.1) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.minVolume'}));

          return true;
        }

        if (newValue < editedLabwareDefinition.workingCapacity) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.wellCapacityError'}));

          return true;
        }

        editedLabwareDefinition.wellCapacity = Math.min(Number(newValue), 999999);
        break;
      case 'maxVolume':
        if (editedLabwareDefinition.workingCapacity === newValue) {
          return true;
        }

        // eslint-disable-next-line no-restricted-globals
        if (!(!isNaN(parseFloat(newValue)) && isFinite(newValue))) {
          return true;
        }

        if (newValue < 0.1) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.minVolume'}));

          return true;
        }

        if (editedLabwareDefinition.wellCapacity < newValue) {
          onShowSnackbar(Feedback.ERROR, intl.formatMessage({id: 'labwareManager.edit.warning.wellCapacityError'}));

          return true;
        }

        editedLabwareDefinition.workingCapacity = Math.min(Number(newValue), 999999);
        break;
      case 'notes':
        if (editedLabwareDefinition.notes === newValue) {
          return true;
        }

        editedLabwareDefinition.notes = newValue;
        break;
      default:
        break;
    }

    const result = await labwareDatabaseBusinessUpdateLabwareDefinition(editedLabwareDefinition);

    if (!result || result?.code !== 0) {
      onShowSnackbar(Feedback.ERROR, result?.description ?? 'Something happened');

      return true;
    }

    setTargetSelectedRowIdsAfterRefetch([editedLabwareDefinition.id]);
    setSelectedRowIds([rowIndex]);

    const newData = [...dataSource];
    newData[rowIndex] = generateData(editedLabwareDefinition);
    setDataSource(newData);
    // triggerRefetch(pageIndex);

    onShowSnackbar(
      Feedback.SUCCESS,
      intl.formatMessage({id: 'plateTypeEditorContainer.updateLabware.notification.success'}),
    );

    labwareDatabaseBusinessRefreshLabwareDefinitions();

    return false;
  };

  const memoizedDataSource = useMemo(
    () =>
      dataSource.map((ele) => ({
        ...ele,
        columnClassName: 'dummy',
      })),
    [dataSource],
  );

  const COLUMNS = useMemo(
    () => [
      {
        Header: 'Plate Name',
        accessor: 'plateName',
        editable: true,
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (data) => {
          const {value, row} = data;
          return (
            <div style={{display: 'flex', alignItems: 'center'}}>
              {value}
              &nbsp;
              {row.original._original.wellBottomData.WellDepth > deepWellThreshold ? <DeepWellLabel /> : ''}
            </div>
          );
        },
        headerStyle: HEADER_STYLE_12,
      },
      {Header: 'Number of Well', accessor: 'numberOfWell', headerStyle: HEADER_STYLE_8},
      {
        Header: 'Well Bottom Profile',
        accessor: 'wellBottomProfile',
        filterDefinition: {
          type: FmlxTable.FilterType.DROPDOWN,
          options: ['All', 'U', 'V', 'Flat'],
        },
        headerStyle: HEADER_STYLE_12,
      },
      {
        Header: 'Well Shape',
        accessor: 'wellShape',
        filterDefinition: {
          type: FmlxTable.FilterType.DROPDOWN,
          options: ['All', 'Circle', 'Rectangle'],
        },
        headerStyle: HEADER_STYLE_8,
      },
      {
        Header: 'Well Capacity',
        accessor: 'wellCapacity',
        editable: true,
        headerStyle: HEADER_STYLE_10,
      },
      {Header: 'Max Volume', accessor: 'maxVolume', editable: true, headerStyle: HEADER_STYLE_8},
      {Header: 'Notes', accessor: 'notes', editable: true, headerStyle: HEADER_STYLE_12},
      {
        Header: 'Date Modified',
        accessor: 'dateModified',
        filterDefinition: {
          type: FmlxTable.FilterType.DATE,
        },
        hideFilter: true, // quick fix for exception if filter type is date
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: (data) => {
          const {value} = data;
          return <>{moment(value).format('YYYY-MM-DD')}</>;
        },
        headerStyle: HEADER_STYLE_12,
      },
      {
        Header: '',
        accessor: 'actionButtons',
        disableSort: true,
        hideSort: true,
        hideFilter: true,
      },
    ],
    [deepWellThreshold],
  );

  return (
    <PageLayout>
      <PageLayout.Header>
        <HeaderLayout>
          <HeaderLayout.Left>
            <DrawerButtonTitle title={intl.formatMessage({id: 'labwareManager.title'})} />
          </HeaderLayout.Left>
          <HeaderLayout.Right>
            <Button
              className="labware-manager-button secondary-button"
              disabled={!selectedRows.length}
              onClick={() => {
                const ids = selectedRows.map((e) => e.id);

                showDeleteConfirmation(ids);
              }}
            >
              {intl.formatMessage({id: 'labwareManager.header.delete'})}
            </Button>
            <Button
              className="labware-manager-button secondary-button"
              disabled={!selectedRows.length}
              onClick={() => {
                const ids = selectedRows.map((e) => e.id);

                duplicateHandler(ids);
              }}
            >
              {intl.formatMessage({id: 'labwareManager.header.duplicate'})}
            </Button>
            {!selectedRows.length ? (
              <Button
                className="labware-manager-button secondary-button"
                onClick={() => {
                  fileInputRef.current.click();
                }}
              >
                {intl.formatMessage({id: 'labwareManager.header.import'})}
              </Button>
            ) : (
              <Button className="labware-manager-button secondary-button" onClick={exportHandler}>
                {intl.formatMessage({id: 'labwareManager.header.export'})}
              </Button>
            )}
            <Button className="labware-manager-button primary-button" onClick={createNewHandler}>
              {intl.formatMessage({id: 'labwareManager.header.createNew'})}
            </Button>
          </HeaderLayout.Right>
        </HeaderLayout>
        <input
          ref={fileInputRef}
          type="file"
          name="import-file"
          onChange={(e) => {
            importHandler(e);
            fileInputRef.current.value = '';
          }}
          accept=".json"
          multiple
          style={{position: 'absolute', opacity: 0, pointerEvents: 'none'}}
        />
      </PageLayout.Header>
      <PageLayout.Content>
        <RemoveLabwareModal
          showModal={showRemoveModal}
          handleClose={() => {
            setDataRemoveModal([]);
            setShowRemoveModal(false);
          }}
          data={dataRemoveModal}
        />
        <FmlxTable
          key={`labware-manager-table-${forceRemountCount}`}
          selectedRowIds={selectedRowIds}
          type={FmlxTable.Type.MULTISELECT}
          data={memoizedDataSource}
          columns={COLUMNS}
          getSelectedRow={(selectedItems) => {
            setSelectedRows(selectedItems);
          }}
          getSortValue={(e) => {
            setSortValue(e.column);
            setSortType(e.type);
            triggerRefetch(pageIndex);
          }}
          showPagination
          pageIndex={pageIndexCount}
          pageSizes={pageSizeFormat}
          fetchData={fetchDataCallback}
          itemsTotal={dataSourceTotalLength}
          showFilter
          getFilterMap={(e) => {
            setFilterMap(e);
            triggerRefetch();
          }}
          placeholder={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <img src="./svg/labware-manager/plate-not-found.svg" alt="plate not found icon" />
              <div
                style={{
                  fontSize: 24,
                  color: '#707070',
                  marginTop: 40,
                }}
              >
                Plate Not Found
              </div>
            </div>
          }
          isLoading={isLoading}
          onInlineEditingChange={onChangeCell}
          onRowDoubleClick={onDoubleClickRow}
          onDropdownPaginationChange={(e) => {
            setPageSize(e);
          }}
          showToolbar={false}
          mappedData={[1, 2]}
        />
      </PageLayout.Content>
    </PageLayout>
  );
}

LabwareManager.propTypes = {
  plateTypeEditorStateLastProcessedId: PropTypes.any,
  labwareConfig: PropTypes.object.isRequired,
  deepWellThreshold: PropTypes.number.isRequired,
  trayType: PropTypes.string.isRequired,
  onChangeView: PropTypes.func.isRequired,
  onHideLoading: PropTypes.func.isRequired,
  onShowLoading: PropTypes.func.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  modalStateActionShowModal: PropTypes.func.isRequired,
  modalStateActionHideModal: PropTypes.func.isRequired,
  onSetLastProcessedId: PropTypes.func.isRequired,
  onRestorePlateTypeEditorState: PropTypes.func.isRequired,
  onFastGetInstalledTrayAndOpenPlateDefinitionPage: PropTypes.func.isRequired,
  labwareBusinessIsLabwareNameAlreadyExist: PropTypes.func.isRequired,
  labwareDatabaseBusinessCreateLabwareDefinitions: PropTypes.func.isRequired,
  labwareDatabaseBusinessReadLabwareDefinitions: PropTypes.func.isRequired,
  labwareDatabaseBusinessUpdateLabwareDefinition: PropTypes.func.isRequired,
  labwareDatabaseBusinessDeleteLabwareDefinitions: PropTypes.func.isRequired,
  labwareDatabaseBusinessDuplicateLabwareDefinitions: PropTypes.func.isRequired,
  labwareDatabaseBusinessRefreshLabwareDefinitions: PropTypes.func.isRequired,
  commonModalBusinessShowModalUnsupportedTray: PropTypes.func.isRequired,
  commonModalBusinessShowUpdatePlateDefinitionDialog: PropTypes.func.isRequired,
  reteachPlateBusinessProceedReteachImportedLabware: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
};

LabwareManager.defaultProps = {
  plateTypeEditorStateLastProcessedId: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(LabwareManager));
